/**
 * Admin Audit Dashboard
 *
 * Cross-collection view over the 3 audit-trail collections (country
 * policy, GDPR consents, dealer role changes). Designed for the LoJack
 * GDPR Officer / IT Admin to answer:
 *   - "What changed in the last 24h?"        → feed table
 *   - "Is anyone doing anything sketchy?"    → alerts panel
 *   - "Export for the EDPS Annual Report"    → CSV download
 *
 * Super-admin gated server-side. Mara CR 22/02/2026 follow-up.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Download, RefreshCw, Filter, ShieldCheck, FileText, Users, ChevronDown, ShieldAlert, Languages, ScrollText, CloudUpload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "../config";

const ENTITY_TYPES = [
  { value: "all", label: "Tutti" },
  { value: "country_config", label: "Country Policy", icon: ShieldCheck },
  { value: "gdpr_consents", label: "GDPR Consents", icon: FileText },
  { value: "dealer_role", label: "Dealer Roles", icon: Users },
  // 28/02/2026 — Fabrizio CR P2 (audit configurazioni): MFA / traduzioni
  // AI / pubblicazioni legal / backup runs ora visibili nello stesso feed.
  { value: "mfa_events", label: "MFA Events", icon: ShieldAlert },
  { value: "translation_publish", label: "AI Translations", icon: Languages },
  { value: "legal_publish", label: "Legal CMS", icon: ScrollText },
  { value: "backup_runs", label: "Backup Runs", icon: CloudUpload },
];

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-950/40", border: "border-red-500/40", text: "text-red-300", badge: "bg-red-500/20 text-red-300" },
  warning:  { bg: "bg-amber-950/40", border: "border-amber-500/40", text: "text-amber-300", badge: "bg-amber-500/20 text-amber-300" },
  info:     { bg: "bg-blue-950/40", border: "border-blue-500/40", text: "text-blue-300", badge: "bg-blue-500/20 text-blue-300" },
};

const ENTITY_BADGE = {
  country_config: "bg-cyan-500/20 text-cyan-300",
  gdpr_consents:  "bg-purple-500/20 text-purple-300",
  dealer_role:    "bg-emerald-500/20 text-emerald-300",
  mfa_events:          "bg-orange-500/20 text-orange-300",
  translation_publish: "bg-indigo-500/20 text-indigo-300",
  legal_publish:       "bg-rose-500/20 text-rose-300",
  backup_runs:         "bg-teal-500/20 text-teal-300",
};

const AdminAuditDashboard = () => {
  const navigate = useNavigate();

  // ── filter state ────────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [actor, setActor] = useState("");
  const [entityType, setEntityType] = useState("all");
  const [entityId, setEntityId] = useState("");
  const [lookbackHours, setLookbackHours] = useState(48);

  // ── data state ──────────────────────────────────────────────────
  const [feed, setFeed] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsMeta, setAlertsMeta] = useState({ scanned: 0, count: 0 });
  const [feedLoading, setFeedLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedDiff, setExpandedDiff] = useState({});

  const feedParams = useMemo(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    actor: actor.trim() || undefined,
    entity_type: entityType,
    entity_id: entityId.trim() || undefined,
    limit: 100,
  }), [dateFrom, dateTo, actor, entityType, entityId]);

  const loadFeed = async () => {
    setFeedLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-dashboard/feed`, { params: feedParams });
      setFeed(res.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento feed");
      setFeed([]);
    } finally {
      setFeedLoading(false);
    }
  };

  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-dashboard/alerts`, { params: { lookback_hours: lookbackHours } });
      setAlerts(res.data?.alerts || []);
      setAlertsMeta({ scanned: res.data?.scanned_rows || 0, count: res.data?.alert_count || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento alert");
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => { loadFeed(); loadAlerts(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadAlerts(); /* eslint-disable-next-line */ }, [lookbackHours]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      // axios+blob so the browser triggers a download instead of
      // trying to JSON-parse the CSV payload.
      const res = await axios.get(`${API}/admin/audit-dashboard/export.csv`, {
        params: { ...feedParams, limit: 10000 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lojack_audit_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, "")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const rows = res.headers?.["x-audit-rows"];
      toast.success(`Esportate ${rows || feed.length} righe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore export CSV");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setDateFrom(""); setDateTo(""); setActor(""); setEntityType("all"); setEntityId("");
  };

  const toggleDiff = (key) => setExpandedDiff((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100 p-6" data-testid="admin-audit-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition"
            data-testid="audit-back-btn">
            <ArrowLeft size={18} /> Torna alla dashboard
          </button>
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            data-testid="audit-export-csv-btn">
            {exporting ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
            Export CSV (EDPS)
          </button>
        </div>

        <h1 className="text-3xl font-semibold mb-2">Audit Dashboard</h1>
        <p className="text-gray-500 text-sm mb-8">
          Cross-collection feed dei cambi su{" "}
          <code className="text-cyan-400">country_config_history</code>,{" "}
          <code className="text-purple-400">gdpr_consents_history</code>,{" "}
          <code className="text-emerald-400">dealer_role_changes</code>,{" "}
          <code className="text-orange-400">mfa_audit_log</code>,{" "}
          <code className="text-indigo-400">translation_jobs</code>,{" "}
          <code className="text-rose-400">legal_publish</code>,{" "}
          <code className="text-teal-400">backup_log</code> + rule-based alert
          per pattern sospetti.
        </p>

        {/* Alerts panel */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-5 mb-6" data-testid="audit-alerts-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-400" />
              Alert Pattern Sospetti
              <span className="text-xs text-gray-500 ml-2 font-normal">
                ({alertsMeta.count} su {alertsMeta.scanned} eventi scansionati)
              </span>
            </h2>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Lookback:</span>
              <select
                value={lookbackHours}
                onChange={(e) => setLookbackHours(Number(e.target.value))}
                className="bg-[#1a1a1a] border border-gray-700 rounded px-2 py-1 text-gray-200"
                data-testid="audit-lookback-select">
                <option value={24}>24h</option>
                <option value={48}>48h</option>
                <option value={168}>7 giorni</option>
                <option value={720}>30 giorni</option>
              </select>
              <button
                onClick={loadAlerts}
                disabled={alertsLoading}
                className="p-1 text-gray-400 hover:text-white"
                data-testid="audit-reload-alerts">
                <RefreshCw size={14} className={alertsLoading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {alertsLoading && (
            <div className="text-center text-gray-500 py-6 text-sm" data-testid="audit-alerts-loading">
              <RefreshCw size={16} className="inline animate-spin mr-2" />Scansione in corso...
            </div>
          )}
          {!alertsLoading && alerts.length === 0 && (
            <div className="text-center text-gray-500 py-6 text-sm" data-testid="audit-alerts-empty">
              ✅ Nessun pattern sospetto rilevato nelle ultime {lookbackHours}h
            </div>
          )}
          {!alertsLoading && alerts.length > 0 && (
            <div className="space-y-2">
              {alerts.map((a, i) => {
                const s = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info;
                return (
                  <div
                    key={a.id || i}
                    className={`${s.bg} ${s.border} border rounded-lg p-3 text-sm`}
                    data-testid={`audit-alert-${i}`}>
                    <div className="flex items-start gap-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${s.badge}`}>
                        {a.severity.toUpperCase()}
                      </span>
                      <div className="flex-1">
                        <div className={s.text}>{a.description}</div>
                        <div className="text-[11px] text-gray-500 mt-1 font-mono">
                          {a.window_start?.slice(0, 19)}
                          {a.window_end && a.window_end !== a.window_start && (
                            <> → {a.window_end.slice(0, 19)}</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-5 mb-6" data-testid="audit-filters-panel">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Filter size={18} className="text-cyan-400" />Filtri
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Da</label>
              <input type="datetime-local" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-gray-700 rounded px-2 py-1.5"
                data-testid="audit-filter-date-from" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">A</label>
              <input type="datetime-local" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-gray-700 rounded px-2 py-1.5"
                data-testid="audit-filter-date-to" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Tipo</label>
              <select value={entityType} onChange={e => setEntityType(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-gray-700 rounded px-2 py-1.5"
                data-testid="audit-filter-entity-type">
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Actor (substring)</label>
              <input type="text" value={actor} onChange={e => setActor(e.target.value)} placeholder="es. mara"
                className="w-full bg-[#1a1a1a] border border-gray-700 rounded px-2 py-1.5"
                data-testid="audit-filter-actor" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Entity ID</label>
              <input type="text" value={entityId} onChange={e => setEntityId(e.target.value)} placeholder="es. IT / customer-id"
                className="w-full bg-[#1a1a1a] border border-gray-700 rounded px-2 py-1.5"
                data-testid="audit-filter-entity-id" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={loadFeed} disabled={feedLoading}
              className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs rounded transition disabled:opacity-50"
              data-testid="audit-apply-filters">
              {feedLoading ? "Caricamento..." : "Applica filtri"}
            </button>
            <button onClick={resetFilters}
              className="px-4 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded transition"
              data-testid="audit-reset-filters">
              Reset
            </button>
          </div>
        </div>

        {/* Feed table */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden" data-testid="audit-feed-table">
          <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Feed Cross-Collection</h2>
            <span className="text-xs text-gray-500">{feed.length} risultati</span>
          </div>

          {feedLoading && (
            <div className="text-center text-gray-500 py-12 text-sm">
              <RefreshCw size={20} className="inline animate-spin mr-2" />Caricamento feed...
            </div>
          )}
          {!feedLoading && feed.length === 0 && (
            <div className="text-center text-gray-500 py-12 text-sm" data-testid="audit-feed-empty">
              Nessun evento trovato per i filtri attuali.
            </div>
          )}
          {!feedLoading && feed.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Timestamp</th>
                    <th className="text-left px-4 py-3 font-medium">Tipo</th>
                    <th className="text-left px-4 py-3 font-medium">Entity ID</th>
                    <th className="text-left px-4 py-3 font-medium">Actor</th>
                    <th className="text-left px-4 py-3 font-medium">Diff</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {feed.map((e, i) => {
                    const key = `${e.entity_type}-${e.entity_id}-${e.updated_at}-${i}`;
                    const expanded = expandedDiff[key];
                    return (
                      <tr key={key} className="hover:bg-[#0a0a0a]" data-testid={`audit-feed-row-${i}`}>
                        <td className="px-4 py-3 font-mono text-[11px] text-gray-400 whitespace-nowrap">
                          {(e.updated_at || "").slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${ENTITY_BADGE[e.entity_type] || "bg-gray-700 text-gray-300"}`}>
                            {e.entity_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-300 font-mono text-xs">{e.entity_id || "—"}</td>
                        <td className="px-4 py-3 text-gray-300 text-xs">{e.updated_by || "—"}</td>
                        <td className="px-4 py-3 text-xs">
                          {e.diff && e.diff.length > 0 ? (
                            <div>
                              <button onClick={() => toggleDiff(key)}
                                className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
                                data-testid={`audit-toggle-diff-${i}`}>
                                {e.diff.length} {e.diff.length === 1 ? "campo" : "campi"}
                                <ChevronDown size={12} className={`transition ${expanded ? "rotate-180" : ""}`} />
                              </button>
                              {expanded && (
                                <div className="mt-2 space-y-1 bg-[#0a0a0a] rounded p-2 border border-gray-800">
                                  {e.diff.map((d, di) => (
                                    <div key={di} className="font-mono text-[11px] flex items-center gap-2 flex-wrap">
                                      <span className="text-cyan-400">{d.field}</span>:
                                      <span className="text-red-400 line-through">
                                        {d.before === null || d.before === undefined ? "∅" : JSON.stringify(d.before)}
                                      </span>
                                      <span className="text-gray-500">→</span>
                                      <span className="text-green-400">{JSON.stringify(d.after)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : e.summary ? (
                            <span className="text-gray-300" data-testid={`audit-summary-${i}`}>
                              {e.summary}
                            </span>
                          ) : <span className="text-gray-600">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAuditDashboard;
